<template>
  <div class="md-card-wrapper">
    <div v-if="cardType !== 'download_button'" class="md-card" :class="[`md-card-${cardType}`, { 'md-card-done': isDone, 'md-card-plain': isPlainCard, 'md-card-clickable': !!cardUiAction }]" @click="handleCardUiAction">
      <!-- confirm type -->
    <template v-if="cardType === 'confirm'">
      <div class="md-card-header">
        <i v-if="icon" :class="icon" class="card-icon"></i>
        <span class="card-title">{{ title }}</span>
      </div>
      <div v-if="displayContent" class="card-desc">{{ displayContent }}</div>
      <div v-if="isDone" class="card-done-hint">
        <i class="el-icon-circle-check"></i>
        <span>{{ $t('workbench.selected', { value: getSelectedLabel() }) }}</span>
      </div>
      <div v-else class="card-actions">
        <button class="card-btn card-btn-confirm" @click="handleAction('confirm')">{{ confirmLabel }}</button>
        <button class="card-btn card-btn-cancel" @click="handleAction('cancel')">{{ cancelLabel }}</button>
      </div>
    </template>

    <!-- radio type -->
    <template v-else-if="cardType === 'radio'">
      <div class="md-card-header">
        <span class="card-title">{{ title }}</span>
      </div>
      <div v-if="isDone" class="card-done-hint">
        <i class="el-icon-circle-check"></i>
        <span>{{ $t('workbench.selected', { value: getSelectedLabel() }) }}</span>
      </div>
      <template v-else>
        <div class="card-options">
          <label v-for="(opt, idx) in parsedOptions" :key="idx" class="card-radio" :class="{ active: selectedValue === (opt.value || opt) }">
            <input type="radio" :name="scopeKey" :value="opt.value || opt" :checked="selectedValue === (opt.value || opt)" @change="handleAction(opt.value || opt)" />
            <span class="radio-label">{{ opt.label || opt }}</span>
          </label>
        </div>
      </template>
    </template>

    <!-- input type -->
    <template v-else-if="cardType === 'input'">
      <div class="md-card-header">
        <span class="card-title">{{ title }}</span>
      </div>
      <div v-if="isDone" class="card-done-hint">
        <i class="el-icon-circle-check"></i>
        <span>{{ $t('workbench.submitted', { value: getSelectedLabel() }) }}</span>
      </div>
      <template v-else>
        <input class="card-input" v-model="inputValue" :placeholder="placeholder || $t('workbench.inputPlaceholderCard')" @keyup.enter="handleSubmitInput" />
        <div class="card-actions">
          <button class="card-btn card-btn-confirm" @click="handleSubmitInput">{{ $t('workbench.submit') }}</button>
        </div>
      </template>
    </template>

    <!-- button type -->
    <template v-else-if="cardType === 'button'">
      <div class="md-card-header">
        <i v-if="icon" :class="icon" class="card-icon"></i>
        <span class="card-title">{{ title }}</span>
      </div>
      <div v-if="displayContent" class="card-desc">{{ displayContent }}</div>
      <div v-if="isDone && !isPlainCard" class="card-done-hint">
        <i class="el-icon-circle-check"></i>
        <span>{{ $t('workbench.selected', { value: getSelectedLabel() }) }}</span>
      </div>
      <div v-else class="card-actions card-actions-buttons">
        <button
          v-for="(btn, idx) in parsedButtons"
          :key="idx"
          class="card-btn"
          :class="{ 'card-btn-confirm': btn.btnType === 'primary' }"
          @click="handleButtonClick(btn)"
        >
          <i v-if="btn.icon" :class="btn.icon" class="card-btn-icon"></i>
          <span>{{ btn.text }}</span>
        </button>
      </div>
    </template>

    <!-- success type -->
    <template v-else-if="cardType === 'success'">
      <div class="md-card-header md-card-header-success">
        <i class="el-icon-circle-check card-icon-success"></i>
        <span class="card-title">{{ title }}</span>
        <i v-if="cardUiAction" class="el-icon-arrow-right card-action-arrow"></i>
      </div>
      <div v-if="displayContent" class="card-desc">{{ displayContent }}</div>
    </template>

    <!-- tips type -->
    <template v-else-if="cardType === 'tips'">
      <div class="md-card-header md-card-header-tips">
        <i class="el-icon-info card-icon-tips"></i>
        <span class="card-title">{{ title }}</span>
      </div>
      <div v-if="displayContent" class="card-desc">{{ displayContent }}</div>
    </template>

    <!-- loading type -->
    <template v-else-if="cardType === 'loading'">
      <!-- 横向步骤条模式（需显式传 stepsLayout="steps"，默认不启用） -->
      <template v-if="isStepsLayoutHorizontal">
        <div class="loading-md-card-header">
          <i :class="loadingProgress >= 100 ? 'el-icon-circle-check' : 'el-icon-loading'" class="loading-header-icon"></i>
          <span class="card-title">{{ title }}</span>
          <span v-if="parsedSteps.length > 0" class="loading-steps-count">
            {{ $t('workbench.executionProgress') }} {{ Math.min(currentStep + (loadingProgress >= 100 ? 0 : 1), parsedSteps.length) }}/{{ parsedSteps.length }}
          </span>
        </div>
        <div v-if="parsedSteps.length > 0" class="loading-body loading-body-step">
          <div class="loading-steps-list">
            <div class="loading-steps-line">
              <div class="loading-steps-line-fill" :class="{ 'is-done': loadingProgress >= 100 }" :style="{ width: parsedSteps.length > 1 ? (currentStep / (parsedSteps.length - 1)) * 100 + '%' : '100%' }"></div>
            </div>
            <div v-for="(step, idx) in parsedSteps" :key="idx" class="loading-step-item"
              :class="{ 'is-done': idx < currentStep, 'is-current': idx === currentStep && loadingProgress < 100, 'is-pending': idx > currentStep }">
              <div class="loading-step-circle" :class="{ 'is-done': idx < currentStep, 'is-current': idx === currentStep && loadingProgress < 100 }">
                <template v-if="idx < currentStep">✓</template>
                <template v-else>{{ idx + 1 }}</template>
              </div>
              <div class="loading-step-content">
                <span class="loading-step-label">{{ step }}</span>
                <span class="loading-step-status">
                  {{ idx < currentStep ? $t('workbench.stepCompleted') : (idx === currentStep && loadingProgress < 100) ? $t('workbench.stepRunning') : $t('workbench.stepWaiting') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>
      <!-- 默认模式：保持原有样式（百分比 + 进度条 + 竖向步骤） -->
      <template v-else>
        <div class="loading-md-card-header">
          <i :class="loadingProgress >= 100 ? 'el-icon-circle-check' : 'el-icon-loading'" class="loading-header-icon"></i>
          <span class="card-title">{{ title }}</span>
        </div>
        <div class="loading-body">
          <!-- 状态图标和百分比 -->
          <div class="loading-status">
            <div class="loading-circle" :class="{ 'is-done': loadingProgress >= 100 }">
              <i :class="loadingProgress >= 100 ? 'el-icon-circle-check' : 'el-icon-loading'" class="loading-circle-icon"></i>
            </div>
            <div class="loading-percent">{{ Math.round(loadingProgress) }}%</div>
            <div class="loading-label">{{ loadingProgress >= 100 ? $t('workbench.completed') : $t('workbench.processing') }}</div>
          </div>
          <!-- 进度条 -->
          <div class="loading-bar-track">
            <div class="loading-bar-fill" :class="{ 'is-done': loadingProgress >= 100 }" :style="{ width: loadingProgress + '%' }"></div>
          </div>
          <!-- 步骤列表 -->
          <div v-if="parsedSteps.length > 0" class="loading-steps-card">
            <div class="loading-steps-header">
              <span class="loading-steps-dot"></span>
              <span class="loading-steps-title">{{ $t('workbench.executionProgress') }} ({{ Math.min(currentStep + (loadingProgress >= 100 ? 0 : 1), parsedSteps.length) }}/{{ parsedSteps.length }})</span>
            </div>
            <div class="loading-steps-list">
              <div class="loading-steps-line">
                <div class="loading-steps-line-fill" :class="{ 'is-done': loadingProgress >= 100 }" :style="{ height: parsedSteps.length > 1 ? (currentStep / (parsedSteps.length - 1)) * 100 + '%' : '100%' }"></div>
              </div>
              <div v-for="(step, idx) in parsedSteps" :key="idx" class="loading-step-item"
                :class="{ 'is-done': idx < currentStep, 'is-current': idx === currentStep && loadingProgress < 100, 'is-pending': idx > currentStep }">
                <div class="loading-step-circle" :class="{ 'is-done': idx < currentStep, 'is-current': idx === currentStep && loadingProgress < 100 }">
                  <template v-if="idx < currentStep">✓</template>
                  <template v-else>{{ idx + 1 }}</template>
                </div>
                <div class="loading-step-content">
                  <span class="loading-step-label">{{ step }}</span>
                  <span class="loading-step-status">
                    {{ idx < currentStep ? $t('workbench.stepCompleted') : (idx === currentStep && loadingProgress < 100) ? $t('workbench.stepRunning') : $t('workbench.stepWaiting') }}
                  </span>
                </div>
                <i v-if="idx === currentStep && loadingProgress < 100" class="el-icon-loading loading-step-spinner"></i>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>

    <!-- loading_dynamic type -->
    <template v-else-if="cardType === 'loading_dynamic'">
      <LoadingDynamic
        :title="title"
        :status-api="statusApi"
        :step-api="stepApi"
        :action="action"
        :next-tip="next_tip"
        :get-time="getTime"
        :is-history="isHistory"
      />
    </template>

    <!-- select_table type -->
    <template v-else-if="cardType === 'select_table'">
      <div class="md-card-header">
        <span class="card-title">{{ title }}</span>
      </div>
      <div v-if="isDone" class="card-done-hint">
        <i class="el-icon-circle-check"></i>
        <span>{{ $t('workbench.selected', { value: getSelectedLabel() }) }}</span>
      </div>
      <template v-else>
        <!-- 双排卡片布局 -->
        <template v-if="parsedLayout === 'grid'">
          <div class="card-grid-wrapper">
            <div v-for="(row, idx) in parsedTableData" :key="idx"
                 class="card-grid-item"
                 :class="{ selected: selectedRowIdx === idx }"
                 @click="selectedRowIdx = idx">
              <div class="card-grid-content">
                <div class="card-grid-icon" v-if="getItemIcon(row)">
                  <i :class="getItemIcon(row)"></i>
                </div>
                <div class="card-grid-info">
                  <div class="card-grid-title">{{ getItemTitle(row) }}</div>
                  <div class="card-grid-subtitle" v-if="getItemSubtitle(row)">{{ getItemSubtitle(row) }}</div>
                </div>
              </div>
              <div class="card-grid-radio">
                <input type="radio" :name="scopeKey + '-table'" :checked="selectedRowIdx === idx" @click.stop="selectedRowIdx = idx" />
              </div>
            </div>
          </div>
        </template>
        <!-- 表格布局 -->
        <template v-else>
          <div class="card-table-wrapper">
            <table class="card-table">
              <thead>
                <tr>
                  <th class="radio-th"></th>
                  <th v-for="(col, idx) in parsedColumns" :key="idx">{{ col }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in parsedTableData" :key="idx"
                    :class="{ selected: selectedRowIdx === idx }"
                    @click="selectedRowIdx = idx">
                  <td class="radio-td">
                    <input type="radio" :name="scopeKey + '-table'" :checked="selectedRowIdx === idx" @click.stop="selectedRowIdx = idx" />
                  </td>
                  <td v-for="(col, cidx) in parsedColumns" :key="cidx">{{ row[col] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
        <div class="card-actions">
          <button class="card-btn card-btn-cancel" @click="selectedRowIdx = null">{{ $t('workbench.cancel') }}</button>
          <button class="card-btn card-btn-confirm" :disabled="selectedRowIdx === null" @click="handleTableConfirm">{{ $t('workbench.confirm') }}</button>
        </div>
      </template>
    </template>

    <!-- editable_form type -->
    <template v-else-if="cardType === 'editable_form'">
      <div class="md-card-header">
        <span class="card-title">{{ title || $t('workbench.editableForm') }}</span>
      </div>
      <div v-if="isFormDone" class="card-done-hint">
        <i class="el-icon-circle-check"></i>
        <span>{{ $t('workbench.submittedSimple') }}</span>
      </div>
      <div v-else-if="editableLoading" class="card-loading">
        <i class="el-icon-loading"></i>
        <span>{{ $t('workbench.loading') }}</span>
      </div>
      <div v-else-if="editableError" class="card-desc" style="color: #F44F4F;">
        <i class="el-icon-warning"></i>
        <span>{{ editableError }}</span>
      </div>
      <template v-else>
        <div v-if="parsedFormFields.length === 0" class="card-desc" style="color: #F44F4F;">
          {{ $t('workbench.noFormConfig') }}
        </div>
        <template v-else>
          <div class="editable-table-wrap">
            <table class="editable-table">
              <thead>
                <tr>
                  <th class="row-index-th">#</th>
                  <th v-for="(field, fidx) in parsedFormFields" :key="fidx" class="editable-th">
                    {{ field.label || field.key }}
                    <span v-if="field.required" class="required-mark">*</span>
                  </th>
                  <th class="row-action-th">{{ $t('workbench.operation') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, ridx) in editingData" :key="ridx" class="editable-tr">
                  <td class="row-index-td">{{ ridx + 1 }}</td>
                  <td v-for="(field, fidx) in parsedFormFields" :key="fidx" class="editable-td">
                    <template v-if="field.type === 'select'">
                      <el-select
                        :value="row[field.key]"
                        size="mini"
                        style="width: 100%;"
                        :placeholder="field.placeholder || ''"
                        @change="onFieldSelectChange(row, field.key, $event)"
                      >
                        <el-option
                          v-for="(opt, oidx) in getFieldOptions(field)"
                          :key="oidx"
                          :label="opt.label || opt"
                          :value="opt.value !== undefined ? opt.value : opt"
                        />
                      </el-select>
                    </template>
                    <template v-else-if="field.type === 'number'">
                      <el-input-number
                        :value="row[field.key]"
                        size="mini"
                        style="width: 100%;"
                        :min="field.min"
                        :max="field.max"
                        :placeholder="field.placeholder || ''"
                        controls-position="right"
                        @change="onFieldInput(row, field.key, $event)"
                      />
                    </template>
                    <template v-else-if="field.type === 'textarea'">
                      <el-input
                        :value="row[field.key]"
                        type="textarea"
                        size="mini"
                        :rows="2"
                        :placeholder="field.placeholder || ''"
                        @input="onFieldInput(row, field.key, $event)"
                      />
                    </template>
                    <template v-else>
                      <el-input
                        :value="row[field.key]"
                        size="mini"
                        :placeholder="field.placeholder || ''"
                        @input="onFieldInput(row, field.key, $event)"
                      />
                    </template>
                  </td>
                  <td class="row-action-td">
                    <button class="editable-btn-delete" :disabled="editingData.length <= 1" @click="deleteRow(ridx)">
                      <i class="el-icon-delete"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="editable-footer">
            <button class="editable-btn-add" @click="addRow">
              <i class="el-icon-plus"></i>
              {{ editableAddBtnText }}
            </button>
            <button class="card-btn card-btn-confirm" :disabled="editingData.length === 0" @click="handleSubmitForm">
              {{ editableSubmitBtnText }}
            </button>
          </div>
        </template>
      </template>
    </template>

    <!-- recommended_questions type -->
    <template v-else-if="cardType === 'recommended_questions'">
      <div class="md-card-header">
        <span class="card-title">{{ title || $t('workbench.recommendedQuestions') }}</span>
      </div>
      <div v-if="description" class="card-desc" style="margin-bottom: 12px;">
        {{ description }}
      </div>
      <div v-if="parsedQuestions.length === 0" class="card-desc" style="color: #F44F4F;">
        {{ $t('workbench.noFormConfig') }}
      </div>
      <div v-else class="recommended-questions-list">
        <div
          v-for="(question, qidx) in parsedQuestions"
          :key="qidx"
          class="recommended-question-item"
        >
          <div class="question-content">
            <i class="el-icon-document"></i>
            <span class="question-text">{{ question.text || question }}</span>
          </div>
          <div class="question-actions">
            <button
              class="question-btn question-btn-copy"
              @click="handleCopyQuestion(question)"
              :title="$t('workbench.copyQuestion')"
            >
              <i class="el-icon-document-copy"></i>
            </button>
            <button
              class="question-btn question-btn-send"
              @click="handleSendQuestion(question)"
              :title="$t('workbench.sendQuestion')"
            >
              <svg class="send-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- supply_chain_summary type -->
    <template v-else-if="cardType === 'supply_chain_summary'">
      <div class="sc-summary-card">
        <div class="sc-summary-header">
          <span class="sc-summary-title">{{ title }}</span>
          <span v-if="parsedTipButton" class="sc-summary-tip-btn" :class="'sc-summary-tip-' + parsedTipButton.type" @click="handleTipButtonClick">
            {{ parsedTipButton.text }}
          </span>
        </div>
        <div class="sc-summary-desc">{{ displayContent }}</div>
      </div>
    </template>

    <!-- indicator_grid type -->
    <template v-else-if="cardType === 'indicator_grid'">
      <div class="indicator-grid-card">
        <div v-if="title" class="indicator-grid-header">
          <span class="indicator-grid-title">{{ title }}</span>
        </div>
        <div class="indicator-grid-wrapper">
          <div v-for="(indicator, idx) in parsedIndicators" :key="idx" class="indicator-grid-item">
            <div class="indicator-item-header">
              <span class="indicator-item-name">{{ indicator.name }}</span>
              <svg v-if="indicator.trend && indicator.trend.length > 1" class="indicator-trend-svg" viewBox="0 0 80 24" preserveAspectRatio="none">
                <polyline :points="getIndicatorTrendPoints(indicator.trend)" fill="none" :stroke="getIndicatorTrendColor(indicator.status)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="indicator-item-value">
              <span class="indicator-value-num">{{ indicator.value }}</span>
              <span v-if="indicator.unit" class="indicator-value-unit">{{ indicator.unit }}</span>
            </div>
            <div class="indicator-item-compare" :class="'indicator-compare-' + indicator.status">
              <span class="compare-label">{{ indicator.compareLabel || '同比' }}</span>
              <span class="compare-value">{{ indicator.compare }}</span>
            </div>
            <span class="indicator-item-status" :class="'indicator-status-' + indicator.status">
              {{ indicator.status === 'normal' ? '正常' : indicator.status === 'warning' ? '预警' : '风险' }}
            </span>
          </div>
        </div>
      </div>
    </template>

    <!-- progress_list type -->
    <template v-else-if="cardType === 'progress_list'">
      <div class="progress-list-card">
        <div v-if="title" class="progress-list-header">
          <span class="progress-list-title">{{ title }}</span>
        </div>
        <div class="progress-list-wrapper">
          <div v-for="(task, idx) in parsedTasks" :key="task.id || idx" class="progress-list-item">
            <div class="progress-item-header">
              <span class="progress-item-name">{{ task.name }}</span>
              <span class="progress-item-status" :class="'progress-status-' + task.statusType">
                {{ task.statusLabel }}
              </span>
            </div>
            <div class="progress-item-bar-track">
              <div class="progress-item-bar-fill" :style="{ width: task.progress + '%', background: task.color }"></div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- evidence_chain type -->
    <template v-else-if="cardType === 'evidence_chain'">
      <div class="evidence-chain-card">
        <div class="evidence-chain-header">
          <span class="evidence-chain-title">{{ title }}</span>
          <span class="evidence-chain-step-count">{{ parsedItems.length }}步</span>
        </div>
        <div class="evidence-chain-timeline">
          <div v-for="(item, idx) in parsedItems" :key="idx" class="evidence-chain-item">
            <div class="evidence-item-content">
              <div class="evidence-item-dot"></div>
              <div class="evidence-item-line" v-if="idx < parsedItems.length - 1"></div>
              <div class="evidence-item-body">
                <div class="evidence-item-header">
                  <span class="evidence-item-name">{{ item.name }}</span>
                  <span v-if="item.tag" class="evidence-item-tag">{{ item.tag }}</span>
                </div>
                <div v-if="item.desc" class="evidence-item-desc">{{ item.desc }}</div>
                <div v-if="item.files && item.files.length > 0" class="evidence-item-files">
                  <a v-for="(file, fIdx) in item.files" :key="fIdx" :href="file.url" target="_blank" class="evidence-file-link">
                    <svg class="evidence-file-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10 9 9 9 8 9"/>
                    </svg>
                    <span>{{ file.name }}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- reference_source type -->
    <template v-else-if="cardType === 'reference_source'">
      <div class="reference-source-card">
        <div class="reference-source-header">
          <span class="reference-source-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
          </span>
          <span class="reference-source-title">{{ title || '引用来源' }}</span>
        </div>
        <div class="reference-source-list">
          <div v-for="(source, idx) in parsedSources" :key="idx" class="reference-source-item">
            <span class="reference-item-index">{{ idx + 1 }}</span>
            <div class="reference-item-content">
              <div class="reference-item-header">
                <span class="reference-item-title">{{ source.title }}</span>
                <span class="reference-item-time">{{ source.time }}</span>
              </div>
              <div v-if="source.desc" class="reference-item-desc">{{ source.desc }}</div>
            </div>
            <a v-if="source.url" :href="source.url" target="_blank" class="reference-item-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </template>

    <!-- solution_list type -->
    <template v-else-if="cardType === 'solution_list'">
      <div class="solution-list-card">
        <div v-for="(solution, idx) in parsedSolutions" :key="idx" class="solution-item" :class="{ 'solution-item-recommended': solution.isRecommended }">
          <div class="solution-header">
            <span class="solution-title">{{ solution.title }}</span>
            <div class="solution-tags">
              <span class="solution-tag solution-tag-score">综合 {{ solution.score }}</span>
              <span v-if="solution.isRecommended" class="solution-tag solution-tag-recommended">推荐方案</span>
            </div>
          </div>
          <div class="solution-grid">
              <div v-for="(item, iIdx) in solution.gridItems" :key="iIdx" class="solution-grid-item">
                <span class="grid-item-label">{{ item.label }}</span>
                <span class="grid-item-value">{{ item.value }}</span>
                <span v-if="item.desc" class="grid-item-desc">{{ item.desc }}</span>
              </div>
            </div>
          <div class="solution-key-points">
            <div v-for="(point, pIdx) in solution.keyPoints" :key="pIdx" class="solution-key-point">
              <span class="point-bullet"></span>
              <span class="point-text">{{ point }}</span>
            </div>
          </div>
          <div v-if="solution.summary" class="solution-summary">{{ solution.summary }}</div>
        </div>
      </div>
    </template>

    <!-- content_card type -->
    <template v-else-if="cardType === 'content_card'">
      <div class="content-card">
        <div class="content-card-header">
          <div class="content-card-header-left">
            <span class="content-card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </span>
            <span class="content-card-title">{{ title }}</span>
          </div>
          <span class="content-card-status">已生成 {{ contentItems.length }} 份</span>
        </div>
        <div v-if="parsedContent.description" class="content-card-desc">{{ parsedContent.description }}</div>
        <div class="content-card-items">
          <div v-for="(item, idx) in contentItems" :key="idx" class="content-card-item">
            <div class="content-item-header" @click="toggleContentItem(idx)">
              <span class="content-item-icon" :class="item.iconType">
                <svg v-if="item.iconType === 'report'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <line x1="9" y1="9" x2="15" y2="9"/>
                  <line x1="9" y1="15" x2="15" y2="15"/>
                </svg>
                <svg v-else-if="item.iconType === 'letter'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 8 12 13 17 8"/>
                  <line x1="12" y1="13" x2="12" y2="3"/>
                </svg>
              </span>
              <span class="content-item-title">{{ item.title }}</span>
              <span class="content-item-action" :class="{ 'content-item-action-expanded': expandedContentItems.includes(idx) }">
                <span class="action-text">{{ expandedContentItems.includes(idx) ? '收起' : '查看详情' }}</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline :points="expandedContentItems.includes(idx) ? '6 15 12 9 18 15' : '6 9 12 15 18 9'"/>
                </svg>
              </span>
            </div>
            <div v-if="expandedContentItems.includes(idx)" class="content-item-detail">
              <div v-for="(detail, dIdx) in item.details" :key="dIdx" class="detail-section">
                <div v-if="detail.type === 'title'" class="detail-title">{{ detail.content }}</div>
                <div v-else-if="detail.type === 'text'" class="detail-text">{{ detail.content }}</div>
                <div v-else-if="detail.type === 'list'" class="detail-list">
                  <div v-for="(li, lIdx) in detail.items" :key="lIdx" class="detail-list-item">{{ li }}</div>
                </div>
                <div v-else-if="detail.type === 'table'" class="detail-table">
                  <table>
                    <tbody>
                      <tr v-for="(row, rIdx) in detail.rows" :key="rIdx">
                        <td v-for="(cell, cIdx) in row" :key="cIdx" class="detail-table-cell">{{ cell }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="content-card-footer">
          <button class="content-card-download-btn" @click="handleExportContentCard">
            <span class="download-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
            </span>
            <span class="download-text">导出沟通材料包</span>
          </button>
        </div>
      </div>
    </template>

    <!-- task-confirmation type -->
    <template v-else-if="cardType === 'task-confirmation'">
      <div class="md-card-header">
        <i class="el-icon-info card-icon-tips"></i>
        <span class="card-title">{{ title }}</span>
      </div>
      <div v-if="displayContent" class="card-desc">{{ displayContent }}</div>
      <div class="task-report-list" v-if="parsedReports.length > 0">
        <button v-for="(report, idx) in parsedReports" :key="idx" class="task-report-btn" @click="handleReportJump(report)">
          <span class="task-report-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
            </svg>
          </span>
          <span class="task-report-text">{{ report.title }}</span>
        </button>
      </div>
      <div class="card-actions" v-if="!isDone">
        <button class="card-btn card-btn-confirm" @click="handleAction('confirm')">{{ confirmLabel }}</button>
        <button class="card-btn card-btn-cancel" @click="handleAction('cancel')">{{ cancelLabel }}</button>
      </div>
      <div class="card-done-hint" v-else>
        <i class="el-icon-circle-check"></i>
        <span>{{ $t('workbench.selected', { value: getSelectedLabel() }) }}</span>
      </div>
    </template>

    <!-- default -->
    <template v-else>
      <div class="md-card-header">
        <span class="card-title">{{ title || $t('workbench.unknownCardType') }}</span>
      </div>
      <div v-if="displayContent" class="card-desc">{{ displayContent }}</div>
    </template>
    </div>

    <!-- download_button type -->
    <template v-if="cardType === 'download_button'">
      <button class="download-btn" @click="handleDownload">
        <span class="download-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </span>
        <span class="download-text">{{ parsedDownloadConfig.text }}</span>
      </button>
    </template>
  </div>
</template>

<script>
import LoadingDynamic from './LoadingDynamic.vue'
import axios from 'axios'
import JSZip from 'jszip'

export default {
  name: 'MdCard',
  components: {
    LoadingDynamic
  },
  props: {
    type: { type: String, default: 'tips' },
    title: { type: String, default: '' },
    content: { type: String, default: '' },
    description: { type: String, default: '' },
    icon: { type: String, default: '' },
    action: { type: String, default: '' },
    data: { type: String, default: '' },
    options: { type: String, default: '' },
    columns: { type: String, default: '' },
    column: { type: String, default: '' },
    steps: { type: String, default: '' },
    // loading 卡片步骤布局：传 "steps" 时使用横向步骤条，默认保持原有样式
    stepsLayout: { type: String, default: '' },
    time: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    select_key: { type: String, default: '' },
    confirmText: { type: String, default: '' },
    cancelText: { type: String, default: '' },
    confirmMessage: { type: String, default: '' },
    cancelMessage: { type: String, default: '' },
    next_step: { type: String, default: '' },
    cardScopeKey: { type: String, default: '' },
    isHistory: { type: Boolean, default: false },
    _componentKey: { type: String, default: '' },
    messageId: { type: [String, Number], default: '' },
    // md-renderer 注入的原始 JSON（所有组件通用）
    _rawJson: { type: String, default: '' },
    // loading_dynamic 相关 props
    statusApi: { type: String, default: '' },
    stepApi: { type: String, default: '' },
    next_tip: { type: String, default: '' },
    getTime: { type: [Number, String], default: 1000 },
    // editable_form 相关 props
    formFields: { type: String, default: '' },
    formData: { type: String, default: '' },
    addBtnText: { type: String, default: '' },
    submitBtnText: { type: String, default: '' },
    api: { type: String, default: '' },
    // recommended_questions 相关 props
    questions: { type: String, default: '' },
    // button 相关 props
    buttons: { type: String, default: '' },
    // 卡片级 UI 动作（仅触发页面交互，不发送消息、不锁定卡片）
    uiAction: { type: String, default: '' },
    // select_table 相关 props
    layout: { type: String, default: 'table' },
    itemIcon: { type: String, default: '' },
    itemTitle: { type: String, default: '' },
    itemSubtitle: { type: String, default: '' },
    // supply_chain_summary 相关 props
    tipButton: { type: String, default: '' },
    // indicator_grid 相关 props
    indicators: { type: String, default: '' },
    // progress_list 相关 props
    tasks: { type: String, default: '' },
    // evidence_chain 相关 props
    items: { type: String, default: '' },
    // reference_source 相关 props
    sources: { type: String, default: '' },
    // solution_list 相关 props
    solutions: { type: String, default: '' },
    // content_card 相关 props
    contentData: { type: String, default: '' },
    // download_button 相关 props
    downloadConfig: { type: String, default: '' },
    // task_confirmation 相关 props
    reports: { type: String, default: '' }
  },
  data() {
    return {
      inputValue: '',
      selectedRowIdx: null,
      loadingProgress: 0,
      loadingTimer: null,
      editingData: [],
      editableLoading: false,
      editableError: '',
      expandedContentItems: []
    }
  },
  computed: {
    cardType() {
      return this.type || 'tips'
    },
    displayContent() {
      return this.content || this.description || ''
    },
    scopeKey() {
      return this._componentKey || this.action || 'card'
    },
    scopedSelectionKey() {
      if (!this.action) return null
      const scope = this._componentKey || this.cardScopeKey || 'global'
      return `${scope}::${this.action}`
    },
    parsedOptions() {
      if (!this.options) return []
      try {
        const decoded = this.options.replace(/&quot;/g, '"').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/\n/g, '')
        return JSON.parse(decoded)
      } catch { return [] }
    },
    parsedSteps() {
      if (!this.steps) return []
      try {
        const decoded = this.steps.replace(/&quot;/g, '"').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/\n/g, '')
        return JSON.parse(decoded)
      } catch { return [] }
    },
    parsedTableData() {
      if (!this.data) return []
      try {
        const decoded = this.data.replace(/&quot;/g, '"').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/\n/g, '')
        const parsed = JSON.parse(decoded)
        if (Array.isArray(parsed)) return parsed
        if (parsed && typeof parsed === 'object' && Array.isArray(parsed.data)) return parsed.data
        return []
      } catch { return [] }
    },
    parsedColumns() {
      if (this.parsedTableData.length === 0) return []
      const allKeys = Object.keys(this.parsedTableData[0])
      if (this.column) {
        try {
          let colArr = this.column
          if (typeof colArr === 'string') colArr = JSON.parse(colArr.replace(/\n/g, ''))
          if (Array.isArray(colArr)) return colArr.filter(k => allKeys.includes(k))
        } catch { /* fall through */ }
      }
      return allKeys
    },
    parsedLayout() {
      return this.layout || 'table'
    },
    confirmLabel() {
      return this.confirmText || this.$t('workbench.confirm')
    },
    cancelLabel() {
      return this.cancelText || this.$t('workbench.cancel')
    },
    // 从 store 的 cardSelections 或历史消息中恢复选中状态
    selectedValue() {
      // 1. 先从 store 的 cardSelections 查（当前会话中的点击记录）
      if (this.scopedSelectionKey) {
        const fromStore = this.$store.state.cosmoClaw.cardSelections[this.scopedSelectionKey]
        if (fromStore !== undefined) return fromStore
      }
      // 2. 从对话历史中恢复：从当前卡片所在消息向后查找 [card-action]
      if (!this.action) return null
      const messages = this.$store.state.cosmoClaw.messages || []

      // 找到当前消息的索引，只搜索该消息及之后的 [card-action]
      let startIndex = -1
      if (this.messageId) {
        for (let i = 0; i < messages.length; i++) {
          if (messages[i].id === this.messageId) {
            startIndex = i
            break
          }
        }
        if (startIndex === -1) return null
      } else {
        startIndex = 0
      }

      for (let i = startIndex; i < messages.length; i++) {
        const msg = messages[i]
        if (msg.type !== 'assistant') continue
        const assistantContent = msg.content || ''
        if (!assistantContent.includes(`"${this.action}"`)) continue
        // 向后查找紧跟的 user 消息（跳过 tool/thinking/toolCall）
        for (let j = i + 1; j < messages.length; j++) {
          const nextMsg = messages[j]
          if (nextMsg.type === 'tool' || nextMsg.type === 'thinking' || nextMsg.type === 'toolCall') continue
          if (nextMsg.type === 'assistant') break
          if (nextMsg.type === 'user') {
            const userContent = (nextMsg.content || '').trim()
            if (userContent.startsWith('[card-action]')) {
              return userContent.slice('[card-action]'.length)
            }
            // button 卡片以裸文本发送，后续 user 消息与某按钮 message 相同则视为已点击
            if (this.cardType === 'button') {
              const matched = this.parsedButtons.find(b => b.message && userContent === b.message)
              if (matched) return matched.message
            }
            break
          }
        }
        break
      }
      return null
    },
    isDone() {
      return this.selectedValue !== null
    },
    currentStep() {
      if (this.parsedSteps.length === 0) return 0
      return Math.floor((this.loadingProgress / 100) * this.parsedSteps.length)
    },
    // loading 卡片是否使用横向步骤条（默认 false，保持原有样式）
    isStepsLayoutHorizontal() {
      return this.stepsLayout === 'steps'
    },
    loadingStoreKey() {
      return `${this.action || 'loading'}::${this.title || ''}`
    },
    // loading 卡片是否已在历史消息中完成（通过静默消息判断）
    loadingAlreadyDone() {
      if (this.cardType !== 'loading' || !this.action) return false
      const messages = this.$store.state.cosmoClaw.messages || []
      // 从当前消息位置开始搜索，避免前面同 action 的卡片干扰
      let startIdx = 0
      if (this.messageId) {
        for (let i = 0; i < messages.length; i++) {
          if (messages[i].id === this.messageId) { startIdx = i; break }
        }
      }
      for (let i = startIdx; i < messages.length; i++) {
        const msg = messages[i]
        if (msg.type !== 'assistant') continue
        if (!(msg.content || '').includes(`"${this.action}"`)) continue
        for (let j = i + 1; j < messages.length; j++) {
          const nextMsg = messages[j]
          if (nextMsg.type === 'tool' || nextMsg.type === 'thinking' || nextMsg.type === 'toolCall') continue
          if (nextMsg.type === 'assistant') break
          if (nextMsg.type === 'user') {
            const content = (nextMsg.content || '').trim()
            if (content.startsWith('[card-action]')) {
              return true
            }
            break
          }
        }
      }
      return false
    },
    isFormDone() {
      if (this.cardType !== 'editable_form') return false
      if (!this.action) return false
      const messages = this.$store.state.cosmoClaw.messages || []
      let startIdx = 0
      if (this.messageId) {
        for (let i = 0; i < messages.length; i++) {
          if (messages[i].id === this.messageId) { startIdx = i; break }
        }
      }
      for (let i = startIdx; i < messages.length; i++) {
        const msg = messages[i]
        if (msg.type !== 'assistant') continue
        if (!(msg.content || '').includes(`"${this.action}"`)) continue
        for (let j = i + 1; j < messages.length; j++) {
          const nextMsg = messages[j]
          if (nextMsg.type === 'tool' || nextMsg.type === 'thinking' || nextMsg.type === 'toolCall') continue
          if (nextMsg.type === 'assistant') break
          if (nextMsg.type === 'user') {
            const content = (nextMsg.content || '').trim()
            if (content.startsWith('[card-action]')) {
              return true
            }
            break
          }
        }
      }
      return false
    },
    parsedConfig() {
      return this._editableParseJson(this._rawJson) || {}
    },
    // 卡片级 UI 动作，优先取 props，其次取原始 JSON
    cardUiAction() {
      const fromProp = this._editableParseJson(this.uiAction)
      if (fromProp && fromProp.name) return fromProp
      const fromRaw = this.parsedConfig.uiAction
      if (fromRaw && fromRaw.name) return fromRaw
      return null
    },
    parsedApi() {
      return this._editableParseJson(this.api) || this.parsedConfig.api || null
    },
    editableDataMapping() {
      return this.parsedConfig.dataMapping || {}
    },
    // editable_form 相关计算属性
    parsedFormFields() {
      const raw = this._editableParseJson(this.formFields) || this.parsedConfig.formFields || []
      return Array.isArray(raw) ? raw : []
    },
    rawFormData() {
      const raw = this._editableParseJson(this.formData) || this.parsedConfig.formData || []
      return Array.isArray(raw) ? raw : []
    },
    editableSubmitBtnText() {
      return this.submitBtnText || this.parsedConfig.submitBtnText || this.$t('workbench.submit')
    },
    editableAddBtnText() {
      return this.addBtnText || this.parsedConfig.addBtnText || this.$t('workbench.addRow')
    },
    // recommended_questions 相关计算属性
    parsedQuestions() {
      let arr = []

      // 1. 从 questions prop 解析
      const fromProp = this._editableParseJson(this.questions)
      if (Array.isArray(fromProp) && fromProp.length > 0) arr = fromProp

      // 2. 从 parsedConfig (即 _rawJson) 兜底
      if (arr.length === 0) {
        const fromRaw = this.parsedConfig.questions
        if (Array.isArray(fromRaw) && fromRaw.length > 0) arr = fromRaw
      }

      // 3. 兼容 description 结构：可能数据在 data 字段下
      if (arr.length === 0) {
        const fromData = this.parsedConfig.data
        if (Array.isArray(fromData) && fromData.length > 0) arr = fromData
      }

      return arr
    },
    // button 相关计算属性
    parsedButtons() {
      const fromProp = this._editableParseJson(this.buttons)
      if (Array.isArray(fromProp)) return fromProp.filter(b => b && b.text)
      const fromRaw = this.parsedConfig.buttons
      if (Array.isArray(fromRaw)) return fromRaw.filter(b => b && b.text)
      return []
    },
    // button 卡片无标题/描述/图标时退化为裸按钮，不渲染卡片外壳
    isPlainCard() {
      return this.cardType === 'button' && !this.title && !this.displayContent && !this.icon
    },
    // supply_chain_summary 相关计算属性
    parsedTipButton() {
      const fromProp = this._editableParseJson(this.tipButton)
      if (fromProp && typeof fromProp === 'object') return fromProp
      const fromRaw = this.parsedConfig.tipButton
      if (fromRaw && typeof fromRaw === 'object') return fromRaw
      return null
    },
    // indicator_grid 相关计算属性
    parsedIndicators() {
      const fromProp = this._editableParseJson(this.indicators)
      if (Array.isArray(fromProp)) return fromProp
      const fromRaw = this.parsedConfig.indicators
      if (Array.isArray(fromRaw)) return fromRaw
      return []
    },
    // progress_list 相关计算属性
    parsedTasks() {
      const fromProp = this._editableParseJson(this.tasks)
      if (Array.isArray(fromProp)) return fromProp
      const fromRaw = this.parsedConfig.tasks
      if (Array.isArray(fromRaw)) return fromRaw
      return []
    },
    // evidence_chain 相关计算属性
    parsedItems() {
      const fromProp = this._editableParseJson(this.items)
      if (Array.isArray(fromProp)) return fromProp
      const fromRaw = this.parsedConfig.items
      if (Array.isArray(fromRaw)) return fromRaw
      return []
    },
    // reference_source 相关计算属性
    parsedSources() {
      const fromProp = this._editableParseJson(this.sources)
      if (Array.isArray(fromProp)) return fromProp
      const fromRaw = this.parsedConfig.sources
      if (Array.isArray(fromRaw)) return fromRaw
      return []
    },
    // solution_list 相关计算属性
    parsedSolutions() {
      const fromProp = this._editableParseJson(this.solutions)
      if (Array.isArray(fromProp)) return fromProp
      const fromRaw = this.parsedConfig.solutions
      if (Array.isArray(fromRaw)) return fromRaw
      return []
    },
    // content_card 相关计算属性
    parsedContent() {
      const fromProp = this._editableParseJson(this.contentData)
      if (fromProp && typeof fromProp === 'object') return fromProp
      const fromRaw = this.parsedConfig.contentData
      if (fromRaw && typeof fromRaw === 'object') return fromRaw
      return {}
    },
    contentItems() {
      return this.parsedContent.items || []
    },
    // download_button 相关计算属性
    parsedDownloadConfig() {
      const fromProp = this._editableParseJson(this.downloadConfig)
      if (fromProp && typeof fromProp === 'object') return fromProp
      const fromRaw = this.parsedConfig.downloadConfig
      if (fromRaw && typeof fromRaw === 'object') return fromRaw
      return { text: '导出', content: '', filename: 'download.txt' }
    },
    // task_confirmation 相关计算属性
    parsedReports() {
      if (!this.reports) return []
      try {
        const decoded = this.reports.replace(/&quot;/g, '"').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/\n/g, '')
        return JSON.parse(decoded)
      } catch { return [] }
    }
  },
  watch: {
    // select_table 默认选中第一行（仅预选，不代表已确认）
    parsedTableData: {
      handler(val) {
        if (this.cardType === 'select_table' && val.length > 0 && this.selectedRowIdx === null && !this.isDone) {
          this.selectedRowIdx = 0
        }
      },
      immediate: true
    }
  },
  mounted() {
    // loading 类型：播放进度动画
    if (this.cardType === 'loading') {
      // 历史消息中已存在静默消息，说明已完成，直接显示 100%
      if (this.isHistory || this.loadingAlreadyDone) {
        this.loadingProgress = 100
        // instant: 即时完成（历史 / 已跑过），父级不需要再补逐段渐显
        this.$emit('loading-done', { instant: true })
        return
      }

      const duration = parseFloat(this.time || '5') * 1000
      const interval = 100
      const totalSteps = duration / interval
      const increment = 100 / totalSteps
      let current = 0

      this.loadingTimer = setInterval(() => {
        current += increment
        if (current >= 100) {
          current = 100
          clearInterval(this.loadingTimer)
          this.loadingTimer = null
          // 通知父级：本卡片进度已跑满（用于放行后续内容的延迟渲染）
          this.$emit('loading-done', { instant: false })
          // 加载完毕后发送静默消息
          if (this.action) {
            const msg = this.next_step
              ? `[card-action]${this.next_step}`
              : '[card-action]confirm'
            document.dispatchEvent(new CustomEvent('md-card-action', {
              detail: { action: this.action, value: 'confirm', message: msg }
            }))
          }
        }
        this.loadingProgress = Math.min(current, 100)
      }, interval)
    }

    // editable_form 初始化：填充数据
    if (this.cardType === 'editable_form') {
      if (this.isFormDone || this.isHistory) {
        this.editingData = JSON.parse(JSON.stringify(this.rawFormData))
      } else if (this.parsedApi) {
        this.fetchEditableData()
      } else {
        this.editingData = JSON.parse(JSON.stringify(this.rawFormData))
      }
    }
  },
  beforeDestroy() {
    if (this.loadingTimer) {
      clearInterval(this.loadingTimer)
      this.loadingTimer = null
    }
  },
  methods: {
    handleAction(value) {
      if (this.isDone) return
      if (this.scopedSelectionKey) {
        this.$store.commit('cosmoClaw/SET_CARD_SELECTION', {
          key: this.scopedSelectionKey,
          value
        })
      }
      const message = `[card-action]${value}`
      document.dispatchEvent(new CustomEvent('md-card-action', {
        detail: { action: this.action, value, message, next_step: this.next_step }
      }))
    },
    // task_confirmation 相关方法
    handleReportJump(report) {
      if (report && report.url) {
        // 前端路由跳转,避免整页刷新导致聊天上下文丢失;.catch 兼容 vue-router 3.x 重复导航异常
        const nav = this.$router.push(report.url)
        if (nav && typeof nav.catch === 'function') nav.catch(() => {})
      }
    },
    getIndicatorTrendPoints(trendData) {
      if (!Array.isArray(trendData) || trendData.length < 2) return ''
      const max = Math.max(...trendData)
      const min = Math.min(...trendData)
      const range = max - min || 1
      const width = 80
      const height = 24
      const padding = 2
      const points = trendData.map((val, idx) => {
        const x = padding + (idx / (trendData.length - 1)) * (width - padding * 2)
        const y = height - padding - ((val - min) / range) * (height - padding * 2)
        return `${x},${y}`
      })
      return points.join(' ')
    },
    getIndicatorTrendColor(status) {
      const colors = {
        normal: '#22c55e',
        warning: '#f59e0b',
        danger: '#ef4444',
        info: '#3b82f6'
      }
      return colors[status] || colors.normal
    },
    handleTipButtonClick() {
      if (this.parsedTipButton && this.parsedTipButton.action) {
        this.handleAction(this.parsedTipButton.action)
      }
    },
    handleSubmitInput() {
      if (!this.inputValue.trim()) return
      this.handleAction(this.inputValue.trim())
    },
    // 卡片级 UI 动作：交给页面宿主处理，不发送消息、不锁定卡片
    handleCardUiAction() {
      if (!this.cardUiAction) return
      const evt = new CustomEvent('cosmo-claw-ui-action', {
        detail: { name: this.cardUiAction.name, params: this.cardUiAction.params || {}},
        cancelable: true
      })
      // dispatchEvent 返回 false 表示事件已被宿主 preventDefault（即动作被接住）
      const handled = !document.dispatchEvent(evt)
      if (!handled) {
        console.warn('[MdCard] 未处理的 UI 动作:', this.cardUiAction.name)
      }
    },
    // button 卡片方法：点击后以裸文本发送按钮 message，整张卡片锁定
    handleButtonClick(btn) {
      if (this.isDone || !btn || !btn.message) return
      if (this.scopedSelectionKey) {
        this.$store.commit('cosmoClaw/SET_CARD_SELECTION', {
          key: this.scopedSelectionKey,
          value: btn.message
        })
      }
      document.dispatchEvent(new CustomEvent('md-card-action', {
        detail: { action: this.action, value: btn.message, message: btn.message }
      }))
    },
    handleTableConfirm() {
      if (this.selectedRowIdx === null || !this.select_key) return
      const selectedRow = this.parsedTableData[this.selectedRowIdx]
      if (selectedRow && selectedRow[this.select_key] !== undefined) {
        this.handleAction(String(selectedRow[this.select_key]))
      }
    },
    getItemIcon(row) {
      if (this.itemIcon && row[this.itemIcon]) return row[this.itemIcon]
      return row.icon || ''
    },
    getItemTitle(row) {
      if (this.itemTitle && row[this.itemTitle]) return row[this.itemTitle]
      return row.name || row.title || row.label || ''
    },
    getItemSubtitle(row) {
      if (this.itemSubtitle && row[this.itemSubtitle]) return row[this.itemSubtitle]
      return row.subtitle || row.desc || row.description || ''
    },
    getSelectedLabel() {
      if (!this.selectedValue) return ''
      if (this.cardType === 'confirm') {
        return this.selectedValue === 'cancel' ? this.cancelLabel : this.confirmLabel
      }
      if (this.cardType === 'radio') {
        const found = this.parsedOptions.find(o => (o.value || o) === this.selectedValue)
        return found ? (found.label || found) : this.selectedValue
      }
      if (this.cardType === 'button') {
        const found = this.parsedButtons.find(b => b.message === this.selectedValue)
        return found ? found.text : this.selectedValue
      }
      return this.selectedValue
    },
    // editable_form 方法
    addRow() {
      const emptyRow = {}
      this.parsedFormFields.forEach(f => {
        emptyRow[f.key] = f.defaultValue !== undefined ? f.defaultValue : (f.type === 'number' ? null : '')
      })
      this.editingData.push(emptyRow)
    },
    deleteRow(index) {
      this.editingData.splice(index, 1)
    },
    handleSubmitForm() {
      if (this.isFormDone) return
      const payload = JSON.stringify(this.editingData)
      const message = `[card-action]${payload}`
      document.dispatchEvent(new CustomEvent('md-card-action', {
        detail: { action: this.action, value: payload, message }
      }))
    },
    onFieldInput(row, fieldKey, value) {
      const rowData = this.editingData.find(r => r === row)
      if (rowData) {
        this.$set(rowData, fieldKey, value)
      }
    },
    onFieldSelectChange(row, fieldKey, event) {
      const rowData = this.editingData.find(r => r === row)
      if (rowData) {
        this.$set(rowData, fieldKey, event)
      }
    },
    async fetchEditableData() {
      const api = this.parsedApi
      if (!api || !api.url) return

      const method = (api.method || 'GET').toUpperCase()
      const url = api.url
      const isAbsoluteUrl = /^https?:\/\//i.test(url)

      this.editableLoading = true
      this.editableError = ''

      try {
        const config = { url, method }
        if (isAbsoluteUrl) config.baseURL = ''
        config.headers = { Satoken: '7e442e3a-6300-4fb6-a2fc-4ff8119f24a4' }

        const params = {}
        if (api.params) {
          Object.keys(api.params).forEach(key => {
            const param = api.params[key]
            params[key] = typeof param === 'object' ? param.value : param
          })
        }

        if (method === 'GET') {
          config.params = params
        } else {
          config.data = params
        }

        const response = await axios(config)
        const rawData = response.data

        // 从响应中提取数据数组
        const mapping = this.editableDataMapping
        const responsePath = mapping.responsePath || 'data.records'
        let dataArray = this._getNestedValue(rawData, responsePath)
        if (!Array.isArray(dataArray)) {
          dataArray = this._getNestedValue(rawData, 'data')
        }
        if (!Array.isArray(dataArray)) {
          dataArray = this._getNestedValue(rawData, 'result')
        }
        if (!Array.isArray(dataArray)) {
          dataArray = Array.isArray(rawData) ? rawData : []
        }

        // 如果 dataMapping 指定了字段映射，转换字段名
        const fieldMapping = mapping.fieldMapping || {}
        if (Object.keys(fieldMapping).length > 0 && dataArray.length > 0) {
          dataArray = dataArray.map(item => {
            const mapped = {}
            Object.keys(fieldMapping).forEach(targetKey => {
              const sourceKey = fieldMapping[targetKey]
              mapped[targetKey] = this._getNestedValue(item, sourceKey)
            })
            // 保留未映射的原始字段
            this.parsedFormFields.forEach(f => {
              if (!(f.key in mapped)) {
                mapped[f.key] = this._getNestedValue(item, f.key)
              }
            })
            return mapped
          })
        }

        if (dataArray.length === 0) {
          this.editableError = this.$t('workbench.noFormConfig')
        } else {
          this.editingData = dataArray
        }
      } catch (e) {
        this.editableError = e.message || this.$t('workbench.requestFailedSimple')
      } finally {
        this.editableLoading = false
      }
    },
    _getNestedValue(data, path) {
      if (!data || !path) return data
      if (typeof path === 'number') return data[path]
      const keys = String(path).split('.')
      let result = data
      for (const key of keys) {
        if (result == null) return undefined
        result = result[key]
      }
      return result
    },
    _editableParseJson(value) {
      if (!value) return null
      if (typeof value === 'object') return value
      if (typeof value !== 'string') return null
      const cleaned = value.replace(/\n/g, '').replace(/&quot;/g, '"').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
      try { return JSON.parse(cleaned) } catch {
        try { return JSON.parse(cleaned.replace(/'/g, '"').replace(/,\s*([}\]])/g, '$1')) } catch { return null }
      }
    },
    getFieldOptions(field) {
      return field.options || []
    },
    // recommended_questions 相关方法
    handleCopyQuestion(question) {
      const text = typeof question === 'object' ? question.text : question
      if (!text) return

      const input = document.createElement('input')
      input.value = text
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)

      this.$message.success(this.$t('workbench.copySuccess'))
    },
    handleSendQuestion(question) {
      const text = typeof question === 'object' ? question.text : question
      if (!text) return

      const message = `${text}`
      document.dispatchEvent(new CustomEvent('md-card-action', {
        detail: { action: this.action, value: text, message }
      }))
    },
    // content_card 相关方法
    toggleContentItem(idx) {
      const index = this.expandedContentItems.indexOf(idx)
      if (index > -1) {
        this.expandedContentItems.splice(index, 1)
      } else {
        this.expandedContentItems.push(idx)
      }
    },
    // download_button 相关方法
    handleDownload() {
      const config = this.parsedDownloadConfig
      if (!config.content) return
      const blob = new Blob([config.content], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = config.filename || 'download.txt'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    handleExportContentCard() {
      const zip = new JSZip()
      const folder = zip.folder('沟通材料包')

      this.contentItems.forEach((item, idx) => {
        let txtContent = ''
        if (item.details) {
          item.details.forEach(detail => {
            if (detail.type === 'title') {
              txtContent += detail.content + '\n'
            } else if (detail.type === 'text') {
              txtContent += detail.content + '\n\n'
            } else if (detail.type === 'list') {
              detail.items.forEach(li => {
                txtContent += '· ' + li + '\n'
              })
              txtContent += '\n'
            } else if (detail.type === 'table') {
              detail.rows.forEach(row => {
                txtContent += row.join('\t') + '\n'
              })
              txtContent += '\n'
            }
          })
        }

        const safeFilename = item.title.replace(/[\\/:*?"<>|]/g, '_')
        const filename = `${safeFilename}.txt`
        folder.file(filename, txtContent, { encoding: 'utf-8' })
      })

      zip.generateAsync({ type: 'blob' }).then(blob => {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = '沟通材料包.zip'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.md-card-wrapper {
  display: block;
}

.md-card {
  display: inline-block;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 16px;
  margin: 8px 0;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;
}

.md-card-clickable {
  cursor: pointer;
}

.md-card-clickable:hover {
  border-color: #3370ff;
  box-shadow: 0 4px 16px rgba(51, 112, 255, 0.16);
}

.md-card-clickable:hover .card-title {
  color: #3370ff;
}

.card-action-arrow {
  margin-left: auto;
  font-size: 14px;
  color: #c0c4cc;
  transition: transform 0.2s, color 0.2s;
}

.md-card-clickable:hover .card-action-arrow {
  color: #3370ff;
  transform: translateX(2px);
}

.md-card-done {
  opacity: 0.8;
}

/* button 卡片裸按钮模式：无标题/描述时去掉卡片外壳 */
.md-card-plain {
  border: none;
  background: transparent;
  box-shadow: none;
  padding: 0;
  margin: 4px 0;
  .md-card-header { display: none; }
  .card-actions { margin-top: 0; }
}

.md-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.md-card-header-success { border-left: 4px solid #3370ff; padding-left: 12px; }
.md-card-header-tips { border-left: 4px solid #F59E0B; padding-left: 12px; }

.card-icon { font-size: 18px; color: #3370ff; }
.card-icon-success { font-size: 18px; color: #3370ff; }
.card-icon-tips { font-size: 18px; color: #F59E0B; }
.card-title { font-size: 14px; font-weight: 600; color: #1f2329; }

.card-desc {
  font-size: 12px;
  color: #374151;
  line-height: 1.6;
  margin-bottom: 12px;
}

.card-done-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #3370ff;
  font-weight: 500;
  font-size: 12px;
  i { font-size: 18px; }
}

.card-actions {
  display: flex;
  gap: 12px;
  margin-top: 12px;
}

.card-btn {
  padding: 8px 20px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid #d9d9d9;
  background: #fff;
  color: #1f2329;
  transition: all 0.2s;
  &:hover { border-color: #3370ff; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.card-btn-confirm {
  background: linear-gradient(270deg, #4A5FF3 0%, #4E86FF 100%);
  color: #fff;
  border: none;
  &:hover { opacity: 0.9; }
  &:disabled { background: #d9d9d9; }
}

.card-actions-buttons {
  flex-wrap: wrap;
}

.card-btn-icon {
  margin-right: 4px;
}

.card-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.card-radio {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  color: #1f2329;
  transition: all 0.2s;
  &:hover { border-color: #3370ff; }
  &.active { border-color: #3370ff; background: #f0f5ff; }
  input { cursor: pointer; }
}

.card-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  margin-bottom: 12px;
  transition: border-color 0.2s;
  &:focus { border-color: #3370ff; }
}

.card-steps {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.card-step {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #999;
  &.active { color: #3370ff; font-weight: 500; }
  &.done { color: #3370ff; }
}

.step-icon { font-size: 14px; }

.loading-progress-bar {
  width: 100%;
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  margin-top: 12px;
  overflow: hidden;
}

.loading-progress-fill {
  height: 100%;
  background: linear-gradient(270deg, #4A5FF3 0%, #4E86FF 100%);
  border-radius: 3px;
  transition: width 0.1s linear;
}

/* loading card 新样式 */
.md-card-loading {
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid #3370ff;
  max-width: 100%;
  width: 100%;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  padding: 0;
  &.md-card-done {
    border-color: transparent;
    opacity: 1;
  }
}

.md-card-loading_dynamic {
  border: none;
  padding: 0;
  background: transparent;
  box-shadow: none;
}

.loading-md-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.loading-header-icon {
  font-size: 16px;
  color: #1890ff;
}

.loading-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loading-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
}

.loading-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  box-shadow: 0 4px 15px rgba(24, 144, 255, 0.4);
  margin-bottom: 16px;
  transition: all 0.3s;
  &.is-done {
    background: linear-gradient(135deg, #4A5FF3 0%, #3A4FD3 100%);
    box-shadow: 0 4px 15px rgba(74, 95, 243, 0.4);
  }
}

.loading-circle-icon {
  font-size: 36px;
  color: #fff;
}

.loading-percent {
  font-size: 30px;
  font-weight: 700;
  color: #1890ff;
  text-align: center;
  .is-done & { color: #3370ff; }
}

.loading-label {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
  text-align: center;
}

.loading-bar-track {
  width: 100%;
  height: 10px;
  background: #f0f0f0;
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 24px;
}

.loading-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #1890ff, #096dd9);
  border-radius: 5px;
  transition: width 0.1s linear;
  &.is-done {
    background: linear-gradient(90deg, #4A5FF3, #3A4FD3);
  }
}

.loading-steps-card {
  width: 100%;
  border-radius: 12px;
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.loading-steps-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.loading-steps-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #3370ff;
}

.loading-steps-title {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

.loading-steps-list {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.loading-steps-line {
  position: absolute;
  top: 12px;
  left: 11px;
  width: 2px;
  height: calc(100% - 12px);
  background: #e5e7eb;
  z-index: 0;
}

.loading-steps-line-fill {
  width: 100%;
  background: linear-gradient(to bottom, #1890ff, #096dd9);
  transition: height 0.5s;
  max-height: 100%;
  &.is-done {
    background: linear-gradient(to bottom, #4A5FF3, #3A4FD3);
  }
}

.loading-step-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  z-index: 1;
  transition: all 0.3s;
  &.is-pending { opacity: 0.6; }
}

.loading-step-circle {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  background: #F7F8FC;
  color: #9ca3af;
  border: 1px solid #E5E7EB;
  transition: all 0.3s;
  &.is-done {
    background: linear-gradient(135deg, #4A5FF3 0%, #3A4FD3 100%);
    color: #fff;
    border: none;
    box-shadow: 0 0 8px rgba(74, 95, 243, 0.4);
  }
  &.is-current {
    background: #AAB5FF;
    color: #fff;
    border: 2px solid #AAB5FF;
  }
}

.loading-step-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.loading-step-label {
  font-size: 12px;
  font-weight: 500;
  transition: color 0.3s;
  color: #6b7280;
  .loading-step-item.is-done & { color: #3A4FD3; }
  .loading-step-item.is-current & { color: #AAB5FF; }
}

.loading-step-status {
  font-size: 11px;
  margin-top: 2px;
  color: #9ca3af;
  .loading-step-item.is-done & { color: #3370ff; }
  .loading-step-item.is-current & { color: #AAB5FF; }
}

.loading-step-spinner {
  color: #3370ff;
  font-size: 16px;
}

/* loading 卡片横向步骤条（仅 stepsLayout="steps" 时生效，覆盖上方默认样式） */
.loading-body-step {
  padding: 18px 20px 20px;
}

.loading-steps-count {
  margin-left: auto;
  font-size: 12px;
  color: #6b7280;
}

.loading-body-step .loading-steps-list {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
}

.loading-body-step .loading-steps-line {
  top: 12px;
  left: 10%;
  right: 10%;
  width: auto;
  height: 2px;
}

.loading-body-step .loading-steps-line-fill {
  width: 0;
  height: 100%;
  background: linear-gradient(to right, #1890ff, #096dd9);
  transition: width 0.5s;
  max-width: 100%;
  max-height: none;
  &.is-done {
    background: linear-gradient(to right, #4A5FF3, #3A4FD3);
  }
}

.loading-body-step .loading-step-item {
  flex: 1;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.loading-body-step .loading-step-content {
  width: 100%;
  align-items: center;
  text-align: center;
}

.loading-body-step .loading-step-label {
  line-height: 1.4;
}

/* supply_chain_summary card styles */
.sc-summary-card {
  padding: 16px;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.sc-summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.sc-summary-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.sc-summary-tip-btn {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: #fff;
  border: 1px solid;

  &.sc-summary-tip-warning {
    color: #dc2626;
    border-color: #fecaca;
    background: #fef2f2;
  }
  &.sc-summary-tip-success {
    color: #16a34a;
    border-color: #bbf7d0;
    background: #f0fdf4;
  }
  &.sc-summary-tip-info {
    color: #2563eb;
    border-color: #bfdbfe;
    background: #eff6ff;
  }
  &.sc-summary-tip-danger {
    color: #dc2626;
    border-color: #fecaca;
    background: #fef2f2;
  }
}

.sc-summary-desc {
  font-size: 12px;
  color: #475569;
  line-height: 1.7;
}

/* indicator_grid card styles */
.indicator-grid-card {
  padding: 16px;
}

.indicator-grid-header {
  margin-bottom: 12px;
}

.indicator-grid-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.indicator-grid-wrapper {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.indicator-grid-item {
  padding: 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.indicator-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.indicator-item-name {
  font-size: 12px;
  color: #64748b;
}

.indicator-trend-svg {
  width: 50px;
  height: 18px;
}

.indicator-item-value {
  display: flex;
  align-items: baseline;
  gap: 2px;
  margin-bottom: 6px;
}

.indicator-value-num {
  font-size: 18px;
  font-weight: 700;
}

.indicator-value-unit {
  font-size: 12px;
  color: #64748b;
}

.indicator-item-compare {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  margin-bottom: 8px;

  &.indicator-compare-normal { color: #16a34a; }
  &.indicator-compare-warning { color: #f59e0b; }
  &.indicator-compare-danger { color: #dc2626; }
}

.compare-label {
  opacity: 0.7;
}

.compare-value {
  font-weight: 500;
}

.indicator-item-status {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 500;

  &.indicator-status-normal {
    background: #dcfce7;
    color: #16a34a;
  }
  &.indicator-status-warning {
    background: #fef3c7;
    color: #d97706;
  }
  &.indicator-status-danger {
    background: #fee2e2;
    color: #dc2626;
  }
}

/* progress_list card styles */
.progress-list-card {
  padding: 16px;
}

.progress-list-header {
  margin-bottom: 12px;
}

.progress-list-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.progress-list-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.progress-list-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.progress-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.progress-item-name {
  font-size: 12px;
  font-weight: 500;
  color: #1e293b;
}

.progress-item-status {
  font-size: 12px;
  font-weight: 500;

  &.progress-status-normal { color: #16a34a; }
  &.progress-status-warning { color: #d97706; }
  &.progress-status-danger { color: #dc2626; }
  &.progress-status-info { color: #2563eb; }
}

.progress-item-bar-track {
  height: 6px;
  background: #f1f5f9;
  border-radius: 3px;
  overflow: hidden;
}

.progress-item-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.5s ease;
}

/* evidence_chain card styles */
.evidence-chain-card {
  padding: 16px;
}

.evidence-chain-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.evidence-chain-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.evidence-chain-step-count {
  font-size: 12px;
  color: #64748b;
}

.evidence-chain-timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.evidence-chain-item {
  display: flex;
}

.evidence-item-content {
  flex: 1;
  display: flex;
  gap: 12px;
  padding-bottom: 20px;
}

.evidence-item-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
  margin-top: 4px;
}

.evidence-item-line {
  width: 2px;
  background: #e2e8f0;
  flex-shrink: 0;
  margin-left: 5px;
  position: absolute;
  top: 16px;
  bottom: 0;
}

.evidence-item-content {
  position: relative;
}

.evidence-item-body {
  flex: 1;
}

.evidence-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.evidence-item-name {
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
}

.evidence-item-tag {
  font-size: 11px;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
}

.evidence-item-desc {
  font-size: 12px;
  color: #475569;
  line-height: 1.6;
  margin-bottom: 8px;
}

.evidence-item-files {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.evidence-file-link {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #3b82f6;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.evidence-file-icon {
  width: 14px;
  height: 14px;
}

/* reference_source card styles */
.reference-source-card {
  padding: 16px;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.reference-source-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.reference-source-icon {
  width: 20px;
  height: 20px;
  color: #3b82f6;
}

.reference-source-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.reference-source-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.reference-source-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px dashed #e2e8f0;

  &:last-child {
    border-bottom: none;
  }
}

.reference-item-index {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e0f2fe;
  color: #0284c7;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.reference-item-content {
  flex: 1;
  min-width: 0;
}

.reference-item-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
}

.reference-item-title {
  font-size: 12px;
  font-weight: 500;
  color: #1e293b;
}

.reference-item-time {
  font-size: 12px;
  color: #94a3b8;
  flex-shrink: 0;
}

.reference-item-desc {
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}

.reference-item-link {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #dcfce7;
  color: #16a34a;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  text-decoration: none;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: #bbf7d0;
  }
}

/* solution_list card styles */
.solution-list-card {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.solution-item {
  padding: 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;

  &.solution-item-recommended {
    background: linear-gradient(180deg, #eff6ff 0%, #ffffff 100%);
    border: 1.5px solid #3b82f6;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.12);
  }
}

.solution-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.solution-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.solution-tags {
  display: flex;
  gap: 6px;
}

.solution-tag {
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
}

.solution-tag-score {
  background: #f1f5f9;
  color: #64748b;
}

.solution-tag-recommended {
  background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
  color: #fff;
}

.solution-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}

.solution-grid-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
  background: #f8fafc;
  border-radius: 8px;

  .solution-item-recommended & {
    background: rgba(59, 130, 246, 0.06);
    border: 1px solid rgba(59, 130, 246, 0.15);
  }
}

.grid-item-label {
  font-size: 12px;
  color: #64748b;
}

.grid-item-value {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.grid-item-desc {
  font-size: 11px;
  color: #64748b;
  line-height: 1.4;
}

.solution-key-points {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-bottom: 0;
}

.solution-key-point {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.point-bullet {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #3b82f6;
  margin-top: 7px;
  flex-shrink: 0;
}

.point-text {
  font-size: 12px;
  color: #475569;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .indicator-grid-wrapper {
    grid-template-columns: repeat(2, 1fr);
  }
}

.card-table-wrapper {
  overflow-x: auto;
}

/* 双排卡片布局样式 */
.card-grid-wrapper {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 8px 0;
  max-width: 100%;
  overflow: hidden;
}

.card-grid-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 0;

  &:hover {
    border-color: #3370ff;
    box-shadow: 0 2px 8px rgba(74, 95, 243, 0.1);
  }

  &.selected {
    border-color: #3370ff;
    background: #f0f3ff;
    box-shadow: 0 0 0 2px rgba(74, 95, 243, 0.2);
  }
}

.card-grid-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.card-grid-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #4a5ff3 100%);
  border-radius: 8px;
  flex-shrink: 0;

  i {
    font-size: 20px;
    color: #fff;
  }
}

.card-grid-info {
  flex: 1;
  min-width: 0;
}

.card-grid-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-grid-subtitle {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
  max-height: 36px;
}

.card-grid-radio {
  flex-shrink: 0;
  margin-left: 8px;

  input[type="radio"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #3370ff;
  }
}

.card-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  min-width: 400px;

  th, td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid #f0f0f0;
  }

  th {
    background: #fafafa;
    font-weight: 600;
    color: #333;
    text-align: center;
  }

  tr { cursor: pointer; }
  tr:hover { background: #f5f7fa; }
  tr.selected { background: #e8f4ff; }
}

.radio-th { width: 40px; }

.radio-td {
  width: 40px;
  text-align: center;
  input[type="radio"] { cursor: pointer; width: 15px; height: 15px; accent-color: #3370ff; }
}

/* editable_form styles */
.card-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 0;
  color: #909399;
  font-size: 12px;
}
.editable-table-wrap {
  width: 100%;
  overflow-x: auto;
  margin-bottom: 12px;
}

.editable-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.editable-table thead th {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  padding: 8px 10px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  text-align: left;
}

.editable-table tbody td {
  border: 1px solid #e5e7eb;
  padding: 6px 8px;
  vertical-align: middle;
}

.editable-tr:hover {
  background: #f9fafb;
}

.row-index-th,
.row-index-td {
  width: 40px;
  text-align: center;
  color: #9ca3af;
}

.row-action-th,
.row-action-td {
  width: 50px;
  text-align: center;
}

.editable-btn-delete {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #ef4444;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  &:hover:not(:disabled) { background: #fef2f2; }
  &:disabled { color: #d1d5db; cursor: not-allowed; }
}

.editable-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.editable-btn-add {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  background: transparent;
  color: #3370ff;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: #3370ff; background: #f0f5ff; }
}

.required-mark {
  color: #f5222d;
  margin-left: 2px;
}

.editable-td .el-input-number--mini {
  width: 100%;
}
.editable-td .el-input-number--mini .el-input-number__increase,
.editable-td .el-input-number--mini .el-input-number__decrease {
  width: 22px;
}
.editable-td .el-input-number--mini .el-input__inner {
  padding-left: 26px;
  padding-right: 26px;
}

/* recommended_questions styles */
.recommended-questions-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.recommended-question-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  transition: all 0.2s;
  &:hover {
    border-color: #3370ff;
    background: #f9fafb;
    box-shadow: 0 2px 8px rgba(74, 95, 243, 0.1);
  }
}

.question-content {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  i {
    color: #9ca3af;
    font-size: 16px;
    flex-shrink: 0;
  }
}

.question-text {
  font-size: 12px;
  color: #374151;
  line-height: 1.5;
  word-break: break-word;
}

.question-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-left: 12px;
}

.question-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 12px;
  &:hover {
    border-color: #3370ff;
    color: #3370ff;
    background: #f0f5ff;
  }
  &.question-btn-copy:hover {
    color: #3370ff;
  }
  &.question-btn-send:hover {
    color: #3370ff;
  }
}

.send-icon {
  display: block;
  margin: 0 auto;
}

/* content_card styles */
.content-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  overflow: hidden;
}

.content-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: #fafafa;
  border-bottom: 1px solid #e8e8e8;
}

.content-card-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.content-card-icon {
  width: 24px;
  height: 24px;
  color: #3b82f6;
}

.content-card-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2329;
}

.content-card-status {
  font-size: 12px;
  color: #64748b;
}

.content-card-desc {
  padding: 12px 16px;
  font-size: 12px;
  color: #475569;
  line-height: 1.6;
}

.content-card-items {
  padding: 0 16px 8px;
}

.content-card-footer {
  display: flex;
  justify-content: flex-start;
  padding: 8px 16px 16px;
  border-top: 1px solid #e8e8e8;
}

.content-card-download-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }

  .download-icon {
    width: 18px;
    height: 18px;
  }

  .download-text {
    line-height: 1;
  }
}

.content-card-item {
  margin-top: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;

  &:first-child {
    margin-top: 0;
  }
}

.content-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #fff;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f8fafc;
  }
}

.content-item-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;

  &.report {
    color: #3b82f6;
  }

  &.letter {
    color: #f59e0b;
  }
}

.content-item-title {
  flex: 1;
  margin-left: 10px;
  font-size: 12px;
  color: #334155;
}

.content-item-action {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #3b82f6;
  font-weight: 500;

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.2s;
  }
}

.content-item-detail {
  padding: 12px;
  background: #f8fafc;
  border-top: 1px solid #e8e8e8;
}

.detail-section {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.detail-title {
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #cbd5e1;
}

.detail-text {
  font-size: 12px;
  color: #475569;
  line-height: 1.7;
}

.detail-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-list-item {
  font-size: 12px;
  color: #475569;
  padding-left: 16px;
  position: relative;

  &::before {
    content: '1.';
    position: absolute;
    left: 0;
    color: #64748b;
    font-weight: 500;
  }
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.detail-table-cell {
  padding: 6px 8px;
  border: 1px solid #e8e8e8;
  color: #475569;
}

/* download_button styles */
.download-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 24px;
  background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);

  &:hover {
    background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

.download-icon {
  width: 18px;
  height: 18px;
  color: #fff;
}

.download-text {
  font-size: 12px;
  font-weight: 500;
  color: #fff;
}

/* task-confirmation styles */
.task-report-list {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  margin: 12px 0;
}

.task-report-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f5f7fa;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 12px;
  color: #1f2329;
  white-space: nowrap;

  &:hover {
    background: #e8f0ff;
    border-color: #4e8cff;
    color: #4e8cff;
  }
}

.task-report-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4e8cff;
  flex-shrink: 0;
}

.task-report-text {
  font-size: 12px;
}
</style>
